/**
 * About Page Component
 *
 * About page for the National Council for Equal Shared Parenting
 * Contains information about the organization, mission, and values
 */

import React from 'react'
import { Box, Container, Typography, Stack } from '@mui/material'
import SEOMetaTags from '@/components/SEOMetaTags'
import { getSEOConfig, getCanonicalUrl } from '@/utils/seo'

/**
 * About Page Component
 */
const About: React.FC = () => {
  // Get SEO configuration for this page
  const seoConfig = getSEOConfig('/about')!
  const canonicalUrl = getCanonicalUrl('/about')

  return (
    <Box component="main" role="main">
      {/* SEO Meta Tags */}
      <SEOMetaTags
        title={seoConfig.title}
        description={seoConfig.description}
        canonical={canonicalUrl}
        ogImage={seoConfig.ogImage}
        keywords={seoConfig.keywords}
        noIndex={seoConfig.noIndex}
      />
      <Container maxWidth="lg">
        <Stack spacing={4} sx={{ py: 8 }}>
          {/* Page Header */}
          <Box textAlign="center">
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: 'primary.main',
              }}
            >
              About NCESP
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{ maxWidth: 800, mx: 'auto' }}
            >
              National Council for Equal Shared Parenting
            </Typography>
          </Box>

          {/* About Content */}
          <Box sx={{ py: 4 }}>
            <Typography
              variant="h4"
              component="h2"
              gutterBottom
              sx={{ fontWeight: 600, mb: 3 }}
            >
              Our Organization
            </Typography>
            <Typography
              variant="body1"
              paragraph
              sx={{ fontSize: '1.125rem', lineHeight: 1.8 }}
            >
              The National Council for Equal Shared Parenting (NCESP) is
              dedicated to advancing policies and practices that support equal
              shared parenting arrangements. We believe that children thrive
              when they have meaningful, ongoing relationships with both
              parents.
            </Typography>
            <Typography
              variant="body1"
              paragraph
              sx={{ fontSize: '1.125rem', lineHeight: 1.8 }}
            >
              Our organization works at the intersection of policy advocacy,
              public education, and community support to create a future where
              equal shared parenting is the norm, not the exception.
            </Typography>
          </Box>

          {/* Mission Section */}
          <Box sx={{ bgcolor: 'grey.50', p: 4, borderRadius: 2 }}>
            <Typography
              variant="h4"
              component="h2"
              gutterBottom
              sx={{ fontWeight: 600, mb: 3 }}
            >
              Our Mission
            </Typography>
            <Typography
              variant="body1"
              paragraph
              sx={{ fontSize: '1.125rem', lineHeight: 1.8 }}
            >
              To promote and protect the fundamental right of children to have
              equal access to both parents through advocacy, education, and
              support for equal shared parenting policies and practices.
            </Typography>
          </Box>

          {/* What We Do */}
          <Box sx={{ py: 4 }}>
            <Typography
              variant="h4"
              component="h2"
              gutterBottom
              sx={{ fontWeight: 600, mb: 3 }}
            >
              What We Do
            </Typography>
            <Stack spacing={3}>
              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: 600, color: 'primary.main' }}
                >
                  Policy Advocacy
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontSize: '1.125rem', lineHeight: 1.8 }}
                >
                  We work with legislators and policymakers to advance laws and
                  policies that support equal shared parenting arrangements and
                  protect children&apos;s rights to maintain relationships with
                  both parents.
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: 600, color: 'primary.main' }}
                >
                  Public Education
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontSize: '1.125rem', lineHeight: 1.8 }}
                >
                  We educate the public, media, and decision-makers about the
                  benefits of equal shared parenting through research, data, and
                  evidence-based advocacy.
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: 600, color: 'primary.main' }}
                >
                  Community Support
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontSize: '1.125rem', lineHeight: 1.8 }}
                >
                  We provide resources, support, and a community for families
                  navigating the challenges of shared parenting arrangements.
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Stack>
      </Container>
    </Box>
  )
}

export default About
